import * as TabsPrimitive from '@radix-ui/react-tabs'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '~/lib/utils'

const Tabs = TabsPrimitive.Root

const tabsListVariants = cva('inline-flex items-center justify-center', {
	variants: {
		variant: {
			default: 'gap-2 rounded bg-white p-2 text-muted-foreground',
			material: 'pt-[4px]',
		},
	},
	defaultVariants: {
		variant: 'default',
	},
})

const TabsList = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.List>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> &
		VariantProps<typeof tabsListVariants>
>(({ variant, className, ...props }, ref) => (
	<TabsPrimitive.List
		ref={ref}
		className={tabsListVariants({ variant, className })}
		{...props}
	/>
))

TabsList.displayName = TabsPrimitive.List.displayName

const tabsTriggerVariants = cva(
	[
		'inline-flex items-center justify-center whitespace-nowrap px-3 py-1 font-medium text-foreground text-sm ring-offset-background transition-all',
		'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
	],
	{
		variants: {
			variant: {
				button:
					'rounded-sm hover:bg-slate-100 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow',
				material:
					'border-transparent border-b-4 py-2 data-[state=active]:border-primary data-[state=active]:font-bold data-[state=active]:text-primary',
			},
		},
		defaultVariants: {
			variant: 'button',
		},
	},
)

const TabsTrigger = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
		VariantProps<typeof tabsTriggerVariants>
>(({ variant, className, ...props }, ref) => (
	<TabsPrimitive.Trigger
		ref={ref}
		className={tabsTriggerVariants({ variant, className })}
		{...props}
	/>
))

TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		ref={ref}
		className={cn(
			'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
			className,
		)}
		{...props}
	/>
))

TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsContent, TabsList, TabsTrigger }
